import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  formatBlogDate,
  getBlogContentLanguage,
  getBlogLabels,
  isPrimaryBlogLanguage,
} from "../blogContent";
import { usePublicBlogPosts } from "../usePublicBlogPosts";
import styles from "./BlogPage.module.css";

const BlogIndexPage: React.FC = () => {
  const { lang = "en" } = useParams<{ lang: string }>();
  const contentLanguage = getBlogContentLanguage(lang);
  const labels = getBlogLabels(lang);
  const { posts } = usePublicBlogPosts();
  const guideLanguage = contentLanguage;
  const guideLabels = {
    en: { title: "Start with a production workflow", shorts: "AI Shorts production", marketing: "Marketing video production" },
    ko: { title: "제작 목적에 맞는 흐름부터 확인하세요", shorts: "AI 쇼츠 제작", marketing: "마케팅 영상 제작" },
    ja: { title: "目的に合う制作フローから確認", shorts: "AIショート制作", marketing: "マーケティング動画制作" },
  }[guideLanguage];

  return (
    <div className={styles.page} lang={contentLanguage}>
      <header className={styles.hero}>
        <p className={styles.eyebrow}>AUTOVID · KNOWLEDGE</p>
        <h1 className={styles.title}>{labels.title}</h1>
        <p className={styles.lead}>{labels.lead}</p>
        {!isPrimaryBlogLanguage(lang) && (
          <p className={styles.fallbackNotice}>{labels.fallback}</p>
        )}
      </header>

      <nav className={styles.guideLinks} aria-label={guideLabels.title}>
        <strong>{guideLabels.title}</strong>
        <Link to={`/${guideLanguage}/ai-shorts-maker`}>{guideLabels.shorts}</Link>
        <Link to={`/${guideLanguage}/marketing-video-maker`}>{guideLabels.marketing}</Link>
      </nav>

      {posts.length === 0 ? (
        <p className={styles.empty}>{labels.empty}</p>
      ) : (
        <section className={styles.grid} aria-label={labels.title}>
          {posts.map((post) => {
            const translation = post.translations[contentLanguage];
            return (
              <article className={styles.card} key={post.slug}>
                {post.image && (
                  <img
                    className={styles.cardImage}
                    src={post.image.path}
                    alt={post.image.alt[contentLanguage]}
                    loading="lazy"
                  />
                )}
                <time className={styles.date} dateTime={post.date}>
                  {formatBlogDate(post.date, contentLanguage)}
                </time>
                <h2 className={styles.cardTitle}>{translation.title}</h2>
                <p className={styles.summary}>{translation.summary}</p>
                <Link className={styles.cardLink} to={`/${lang}/blog/${post.slug}`}>
                  {labels.readMore}
                </Link>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
};

export default BlogIndexPage;
